import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { apiCall } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function GiveawayCodeManager({ user, onLogout }) {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  
  // Bulk input state
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [inputMethod, setInputMethod] = useState('csv');
  const [csvText, setCsvText] = useState('');
  const [manualRows, setManualRows] = useState([{merchant: '', code: ''}]);
  const [autoGenerateText, setAutoGenerateText] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/giveaway');
      return;
    }
    
    fetchData();
  }, [campaignId, user, navigate]);

  const fetchData = async () => {
    try {
      const [campaignRes, codesRes] = await Promise.all([
        apiCall(`/api/giveaway/campaigns/${campaignId}`),
        apiCall(`/api/giveaway/campaigns/${campaignId}/codes`)
      ]);

      if (campaignRes.ok && codesRes.ok) {
        const campaignData = await campaignRes.json();
        const codesData = await codesRes.json();
        
        setCampaign(campaignData);
        setCodes(codesData.codes);
      } else {
        toast.error('Failed to load campaign');
        navigate('/giveaway');
      }
    } catch (error) {
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  };

  const generateCodesFromInput = () => {
    let codesList = [];
    
    if (inputMethod === 'csv') {
      // Parse CSV: merchant_name,code
      const lines = csvText.trim().split('\n');
      lines.forEach((line, index) => {
        if (index === 0 && line.toLowerCase().includes('merchant')) return; // Skip header
        const [merchant, code] = line.split(',').map(s => s.trim());
        if (merchant && code) {
          codesList.push({merchant_name: merchant, code: code});
        }
      });
    } else if (inputMethod === 'manual') {
      // Use manual rows
      codesList = manualRows
        .filter(row => row.merchant && row.code)
        .map(row => ({merchant_name: row.merchant, code: row.code}));
    } else if (inputMethod === 'auto') {
      // Auto-generate from merchant names
      const merchantNames = autoGenerateText.trim().split('\n').filter(n => n.trim());
      codesList = merchantNames.map(name => {
        const cleanCode = name
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, '')
          .toUpperCase();
        return {merchant_name: name.trim(), code: cleanCode};
      });
    }
    
    return codesList;
  };

  const handleBulkCreate = async () => {
    const codesList = generateCodesFromInput();
    
    if (codesList.length === 0) {
      toast.error('No valid codes to generate');
      return;
    }
    
    setGenerating(true);

    try {
      const formData = new FormData();
      formData.append('codes_data', JSON.stringify(codesList));

      const response = await apiCall(`/api/giveaway/campaigns/${campaignId}/codes/bulk`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const textData = await response.text();
        const result = JSON.parse(textData);
        
        toast.success(`Created ${result.created} codes!`);
        if (result.errors.length > 0) {
          toast.error(`${result.errors.length} errors: ${result.errors[0]}`);
        }
        
        setShowBulkInput(false);
        setCsvText('');
        setAutoGenerateText('');
        setManualRows([{merchant: '', code: ''}]);
        fetchData();
      } else {
        toast.error(`Failed to create codes: Error ${response.status}`);
      }
    } catch (error) {
      console.error('[Bulk Create] Error:', error);
      toast.error('Network error');
    } finally {
      setGenerating(false);
    }
  };

  const toggleCodeStatus = async (code) => {
    const newStatus = code.status === 'active' ? 'revoked' : 'active';
    
    try {
      const formData = new FormData();
      formData.append('status', newStatus);

      const response = await apiCall(`/api/giveaway/codes/${code.id}/status`, {
        method: 'PATCH',
        body: formData,
      });

      if (response.ok) {
        toast.success(`Code ${newStatus === 'active' ? 'activated' : 'revoked'}`);
        fetchData();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const addManualRow = () => {
    setManualRows([...manualRows, {merchant: '', code: ''}]);
  };

  const updateManualRow = (index, field, value) => {
    const newRows = [...manualRows];
    newRows[index][field] = value;
    setManualRows(newRows);
  };

  const filteredCodes = codes.filter(code => {
    const matchesSearch = searchQuery === '' ||
      code.full_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      code.merchant_name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || code.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const exportCSV = async () => {
    try {
      const response = await apiCall(`/api/giveaway/campaigns/${campaignId}/export-csv`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${campaignId}_codes_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV exported!');
      } else {
        toast.error(`Export failed: Error ${response.status}`);
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const exportZIP = async () => {
    try {
      toast.success('Generating QR codes... Please wait');
      
      const response = await apiCall(`/api/giveaway/campaigns/${campaignId}/export-zip`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${campaignId}_qr_codes_${new Date().toISOString().split('T')[0]}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('QR codes ZIP downloaded!');
      } else {
        toast.error(`Export failed: Error ${response.status}`);
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const downloadIndividualQR = async (code) => {
    try {
      // Generate QR code client-side using the existing QR library
      const QRCode = require('qrcode');
      const shortUrl = `https://sparkqr.app/r/${code.short_code}`;
      
      // Generate QR as data URL
      const qrDataUrl = await QRCode.toDataURL(shortUrl, {
        errorCorrectionLevel: 'H',
        width: 1000,
        color: {
          dark: '#626ed4',
          light: '#FFFFFF'
        }
      });
      
      // Download
      const a = document.createElement('a');
      a.href = qrDataUrl;
      a.download = `${code.full_code}.png`;
      a.click();
      
      toast.success('QR code downloaded!');
    } catch (error) {
      console.error('[QR Download] Error:', error);
      toast.error('Download failed');
    }
  };

  const toggleSelectCode = (codeId) => {
    if (selectedCodes.includes(codeId)) {
      setSelectedCodes(selectedCodes.filter(id => id !== codeId));
    } else {
      setSelectedCodes([...selectedCodes, codeId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedCodes.length === filteredCodes.length) {
      setSelectedCodes([]);
    } else {
      setSelectedCodes(filteredCodes.map(c => c.id));
    }
  };

  const bulkRevoke = async () => {
    if (selectedCodes.length === 0) {
      toast.error('No codes selected');
      return;
    }
    
    if (!window.confirm(`Revoke ${selectedCodes.length} selected codes?`)) return;
    
    try {
      const formData = new FormData();
      formData.append('code_ids', JSON.stringify(selectedCodes));

      const response = await apiCall('/api/giveaway/codes/bulk-revoke', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success(`${selectedCodes.length} codes revoked!`);
        setSelectedCodes([]);
        fetchData();
      } else {
        toast.error('Bulk revoke failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await apiCall(`/api/giveaway/campaigns/${campaignId}/analytics`);
      if (response.ok) {
        const textData = await response.text();
        const data = JSON.parse(textData);
        setAnalytics(data);
        setShowAnalytics(true);
      }
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} user={user} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle={campaign.name} />
        
        <div className="page-content">
          {/* Breadcrumb */}
          <nav className="breadcrumb">
            <span className="breadcrumb-item" onClick={() => navigate('/giveaway')} style={{cursor:'pointer'}}>Giveaway</span>
            <span className="breadcrumb-item active">{campaign.name}</span>
          </nav>

          {/* Campaign Info */}
          <div className="card mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">{campaign.name}</h5>
                <p className="text-muted mb-0">🌐 {campaign.base_domain}</p>
              </div>
              <div className="d-flex gap-2">
                <div className="stat-card" style={{padding:'1rem', minWidth:'100px'}}>
                  <h4>{campaign.total_codes || 0}</h4>
                  <p style={{fontSize:'0.75rem', margin:0}}>Codes</p>
                </div>
                <div className="stat-card success" style={{padding:'1rem', minWidth:'100px'}}>
                  <h4>{campaign.active_codes || 0}</h4>
                  <p style={{fontSize:'0.75rem', margin:0}}>Active</p>
                </div>
                <div className="stat-card info" style={{padding:'1rem', minWidth:'100px'}}>
                  <h4>{campaign.total_scans || 0}</h4>
                  <p style={{fontSize:'0.75rem', margin:0}}>Scans</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={() => setShowBulkInput(!showBulkInput)}
              >
                {showBulkInput ? '✕ Cancel' : '+ Add Codes'}
              </button>
              <button 
                className="btn btn-light"
                onClick={() => {
                  setShowAnalytics(!showAnalytics);
                  if (!showAnalytics && !analytics) fetchAnalytics();
                }}
              >
                📊 {showAnalytics ? 'Hide' : 'Show'} Analytics
              </button>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={exportCSV}>📤 Export CSV</button>
              <button className="btn btn-info" onClick={exportZIP}>📦 Export QR ZIP</button>
            </div>
          </div>

          {/* Analytics Panel */}
          {showAnalytics && analytics && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">📊 Campaign Analytics</h5>
              </div>
              
              <div className="row mb-3">
                <div className="col-md-3">
                  <div className="stat-card">
                    <h3>{analytics.totalScans}</h3>
                    <p>Total Scans</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-card success">
                    <h3>{analytics.totalCodes}</h3>
                    <p>Total Codes</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-card info">
                    <h3>{analytics.activeCodes}</h3>
                    <p>Active Codes</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="stat-card warning">
                    <h3>{analytics.totalCodes > 0 ? Math.round(analytics.totalScans / analytics.totalCodes) : 0}</h3>
                    <p>Avg Scans/Code</p>
                  </div>
                </div>
              </div>
              
              {Object.keys(analytics.scansByCode || {}).length > 0 && (
                <div className="mb-3">
                  <h6 className="mb-2">Top Performing Codes</h6>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={Object.entries(analytics.scansByCode).map(([code, scans]) => ({code, scans}))}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="code" style={{fontSize:'0.75rem'}} />
                      <YAxis style={{fontSize:'0.75rem'}} />
                      <Tooltip />
                      <Bar dataKey="scans" fill="#02a499" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              
              {Object.keys(analytics.scansByDevice || {}).length > 0 && (
                <div className="row mb-3">
                  <div className="col-md-6">
                    <h6 className="mb-2">Scans by Device</h6>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={Object.entries(analytics.scansByDevice).map(([device, scans]) => ({device, scans}))}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="device" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="scans" fill="#626ed4" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="col-md-6">
                    <h6 className="mb-2">Scans by Country</h6>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={Object.entries(analytics.scansByCountry).map(([country, scans]) => ({country, scans}))}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="country" style={{fontSize:'0.75rem'}} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="scans" fill="#38a4f8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bulk Input Panel (Inline) */}
          {showBulkInput && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Add Codes</h5>
              </div>

              {/* Input Method Selector */}
              <div className="nav-tabs mb-3">
                <button className={`nav-tab ${inputMethod==='csv'?'active':''}`} onClick={()=>setInputMethod('csv')}>
                  📄 CSV Upload
                </button>
                <button className={`nav-tab ${inputMethod==='manual'?'active':''}`} onClick={()=>setInputMethod('manual')}>
                  ✏️ Manual Entry
                </button>
                <button className={`nav-tab ${inputMethod==='auto'?'active':''}`} onClick={()=>setInputMethod('auto')}>
                  ⚡ Auto-Generate
                </button>
              </div>

              {/* CSV Method */}
              {inputMethod === 'csv' && (
                <div>
                  <label className="label">Paste CSV Data</label>
                  <textarea
                    className="input"
                    rows="6"
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                    placeholder="merchant_name,code&#10;Joe's Pizza,JOESPIZZA&#10;ABC Store,ABC"
                  />
                  <p className="text-muted" style={{fontSize:'0.75rem', marginTop:'0.5rem'}}>
                    Format: merchant_name,code (one per line)
                  </p>
                </div>
              )}

              {/* Manual Method */}
              {inputMethod === 'manual' && (
                <div>
                  {manualRows.map((row, index) => (
                    <div key={index} className="row mb-2">
                      <div className="col-md-6">
                        <input
                          className="input"
                          value={row.merchant}
                          onChange={(e) => updateManualRow(index, 'merchant', e.target.value)}
                          placeholder="Merchant Name"
                        />
                      </div>
                      <div className="col-md-6">
                        <input
                          className="input"
                          value={row.code}
                          onChange={(e) => updateManualRow(index, 'code', e.target.value)}
                          placeholder="CODE"
                        />
                      </div>
                    </div>
                  ))}
                  <button className="btn btn-light btn-sm" onClick={addManualRow}>
                    + Add Another Row
                  </button>
                </div>
              )}

              {/* Auto-Generate Method */}
              {inputMethod === 'auto' && (
                <div>
                  <label className="label">Paste Merchant Names (one per line)</label>
                  <textarea
                    className="input"
                    rows="6"
                    value={autoGenerateText}
                    onChange={(e) => setAutoGenerateText(e.target.value)}
                    placeholder="Joe's Pizza&#10;ABC Store&#10;XYZ Restaurant"
                  />
                  <p className="text-muted" style={{fontSize:'0.75rem', marginTop:'0.5rem'}}>
                    Codes will be auto-generated from names. Prefix "{campaign.code_prefix}" will be added.
                  </p>
                  
                  {/* Preview */}
                  {autoGenerateText && (
                    <div className="alert alert-info mt-3">
                      <strong>Preview:</strong><br/>
                      {autoGenerateText.trim().split('\n').slice(0,3).map((name, i) => {
                        const code = name.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '').toUpperCase();
                        return <div key={i}>{name} → {campaign.code_prefix}{code}</div>;
                      })}
                      {autoGenerateText.trim().split('\n').length > 3 && <div>...and {autoGenerateText.trim().split('\n').length - 3} more</div>}
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-light" onClick={() => setShowBulkInput(false)}>Cancel</button>
                <button className="btn btn-success" onClick={handleBulkCreate} disabled={generating}>
                  {generating ? 'Generating...' : `Generate ${generateCodesFromInput().length} Codes`}
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="card mb-3" style={{padding:'1rem', background:'var(--gray-100)', border:'1px solid var(--border-color)'}}>
            <div className="row">
              <div className="col-md-8">
                <div className="search-box">
                  <span className="search-box-icon">🔍</span>
                  <input
                    className="input"
                    placeholder="Search by code or merchant..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="all">📊 All Status</option>
                  <option value="active">🟢 Active Only</option>
                  <option value="revoked">🔴 Revoked</option>
                </select>
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-2">
              <p className="text-muted mb-0" style={{fontSize:'0.875rem'}}>
                Showing {filteredCodes.length} of {codes.length} codes
              </p>
              {selectedCodes.length > 0 && (
                <button className="btn btn-danger btn-sm" onClick={bulkRevoke}>
                  🔴 Revoke {selectedCodes.length} Selected
                </button>
              )}
            </div>
          </div>

          {/* Code List */}
          <div className="card">
            {filteredCodes.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🎟️</div>
                <h3>No codes yet</h3>
                <p className="text-muted">Add codes to start tracking QR scans</p>
                <button className="btn btn-primary" onClick={() => setShowBulkInput(true)}>
                  + Add Codes
                </button>
              </div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table>
                  <thead>
                    <tr style={{background:'var(--gray-100)'}}>
                      <th style={{padding:'1rem 0.75rem', width:'40px'}}>
                        <input 
                          type="checkbox" 
                          checked={selectedCodes.length === filteredCodes.length && filteredCodes.length > 0}
                          onChange={toggleSelectAll}
                          style={{width:'18px', height:'18px', cursor:'pointer'}}
                        />
                      </th>
                      <th style={{padding:'1rem 0.75rem'}}>CODE</th>
                      <th style={{padding:'1rem 0.75rem'}}>MERCHANT</th>
                      <th style={{padding:'1rem 0.75rem'}}>TRACKING URL</th>
                      <th style={{padding:'1rem 0.75rem', textAlign:'center'}}>SCANS</th>
                      <th style={{padding:'1rem 0.75rem'}}>STATUS</th>
                      <th style={{padding:'1rem 0.75rem', textAlign:'center'}}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCodes.map((code) => (
                      <tr key={code.id}>
                        <td style={{padding:'0.75rem'}}>
                          <input 
                            type="checkbox" 
                            checked={selectedCodes.includes(code.id)}
                            onChange={() => toggleSelectCode(code.id)}
                            style={{width:'18px', height:'18px', cursor:'pointer'}}
                          />
                        </td>
                        <td style={{padding:'0.75rem', fontFamily:'monospace', fontWeight:'600'}}>
                          {code.full_code}
                        </td>
                        <td style={{padding:'0.75rem'}}>
                          {code.merchant_name}
                        </td>
                        <td style={{padding:'0.75rem', fontSize:'0.75rem', maxWidth:'300px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                          {code.tracking_url}
                        </td>
                        <td style={{padding:'0.75rem', textAlign:'center'}}>
                          <strong style={{color:'var(--success)'}}>{code.total_scans || 0}</strong>
                        </td>
                        <td style={{padding:'0.75rem'}}>
                          {code.status === 'active' ? (
                            <span className="badge badge-success">ACTIVE</span>
                          ) : (
                            <span className="badge badge-danger">REVOKED</span>
                          )}
                        </td>
                        <td style={{padding:'0.75rem', textAlign:'center'}}>
                          <div className="d-flex gap-1 justify-content-center">
                            <button
                              className={`btn btn-sm ${code.status==='active'?'btn-warning':'btn-success'}`}
                              onClick={() => toggleCodeStatus(code)}
                              title={code.status==='active'?'Revoke':'Activate'}
                            >
                              {code.status==='active'?'⚡':'✓'}
                            </button>
                            <button 
                              className="btn btn-sm btn-primary" 
                              onClick={() => downloadIndividualQR(code)}
                              title="Download QR"
                            >
                              📥
                            </button>
                            <button className="btn btn-sm btn-light" 
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                
                                const url = code.tracking_url;
                                
                                // Proper async error handling to prevent Sentry unhandled rejection
                                try {
                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    await navigator.clipboard.writeText(url);
                                    toast.success('URL copied!');
                                  } else {
                                    // No clipboard API - use fallback
                                    const textarea = document.createElement('textarea');
                                    textarea.value = url;
                                    document.body.appendChild(textarea);
                                    textarea.select();
                                    document.execCommand('copy');
                                    document.body.removeChild(textarea);
                                    toast.success('URL copied!');
                                  }
                                } catch (error) {
                                  // Fallback for permission denied
                                  const textarea = document.createElement('textarea');
                                  textarea.value = url;
                                  textarea.style.position = 'fixed';
                                  textarea.style.opacity = '0';
                                  document.body.appendChild(textarea);
                                  textarea.select();
                                  try {
                                    document.execCommand('copy');
                                    toast.success('URL copied!');
                                  } catch (e) {
                                    // Last resort - show in alert
                                    toast.error('Please copy manually');
                                    setTimeout(() => alert(`Copy this URL:\n\n${url}`), 100);
                                  }
                                  document.body.removeChild(textarea);
                                }
                              }}
                              title="Copy URL"
                            >
                              📋
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GiveawayCodeManager;
